import { useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { useT } from "../lib/i18n";

/** Lo que puede depender de lo que el usuario acaba de teclear. */
type Vivo<T> = T | ((valor: number) => T);
const resolver = <T,>(x: Vivo<T>, v: number): T =>
  typeof x === "function" ? (x as (valor: number) => T)(v) : x;

export type Campo = {
  /** cantidad = un importe en el token de referencia · porcentaje = un deslizador 1-100 ·
   *  check = una casilla, que llega a `detalles`/`aviso`/`ejecutar` como 1 o 0 */
  tipo: "cantidad" | "porcentaje" | "check";
  etiqueta: string;
  /** el simbolo del token, a la derecha del campo */
  sufijo?: string;
  /** tope, y el boton "todo" que lo rellena de un clic */
  max?: number;
  /** valor exacto para MÁX (sin pasar por Number), si se conoce */
  maxTexto?: string;
  maxEtiqueta?: string;
  ayuda?: ReactNode;
  inicial?: number;
};

export type Peticion = {
  titulo: string;
  /** Badge de icono a la izquierda del título (le da cara a cada modal). */
  icono?: ReactNode;
  /** Que va a pasar, en una frase. Sin adornos: el usuario esta a punto de firmar. */
  descripcion: Vivo<ReactNode>;
  /** El importe se teclea AQUI DENTRO, no fuera. Ver la nota de arriba del componente. */
  campo?: Campo;
  /** Lo que cambia, en columna. Es lo que se compara de un vistazo antes de firmar. */
  detalles?: Vivo<Array<[string, string]>>;
  /** Lo que conviene saber ANTES, no despues. Sale en rojo. */
  aviso?: Vivo<ReactNode>;
  boton: string;
  /** Una segunda salida para la misma pantalla (retirar vs reinvertir). */
  secundario?: { texto: string; ejecutar: () => Promise<void> | void };
  peligro?: boolean;
  /** Con campo, por defecto exige > 0. */
  valido?: (valor: number) => boolean;
  ejecutar: (valor: string) => Promise<void> | void;
};

/**
 * Confirmacion para todo lo que se firma, y desde el 2026-08-15 tambien el sitio donde se TECLEA
 * el importe.
 *
 * Antes el importe iba en una tarjeta de la pantalla y el modal solo confirmaba, asi que las cifras
 * del resumen se calculaban con un numero que ya no estaba a la vista: para comprobarlas habia que
 * cerrar el modal. Ahora el campo vive dentro y el resumen se recalcula mientras escribes, que es
 * la unica forma de que "a tu wallet llegan X" signifique algo.
 *
 * Uso:
 *   const { pedir, modal } = useConfirmar();
 *   <button onClick={() => pedir({ titulo, descripcion, campo, detalles, boton, ejecutar })}>…</button>
 *   {modal}
 */
export function useConfirmar() {
  const t = useT();
  const [pet, setPet] = useState<Peticion | null>(null);
  const [txt, setTxt] = useState("");

  const valor = Number(txt) || 0;

  function cerrar() {
    setPet(null);
    setTxt("");
  }

  async function lanzar(fn: () => Promise<void> | void) {
    // Se cierra YA y el modal de proceso (logo de Keel cargando) toma el relevo: firmar, confirmar
    // en la cadena y el resultado se enseñan ahí. El error lo gestiona ese proceso, no este modal.
    setPet(null);
    setTxt("");
    try { await fn(); } catch { /* lo recoge acciones.progreso */ }
  }

  const campo = pet?.campo;
  // Una casilla desmarcada es una respuesta VALIDA, no un campo vacio: atarla al `valor > 0` de los
  // importes dejaria el boton muerto justo en la opcion por defecto.
  const listo =
    !campo || (pet?.valido ? pet.valido(valor) : campo.tipo === "check" ? true : valor > 0);

  const modal = pet ? (
    <Modal
      abierto
      cerrar={cerrar}
      titulo={pet.titulo}
      icono={pet.icono}
      ancho={540}
      pie={
        <>
          <button className="btn" onClick={cerrar}>{t("Cancelar")}</button>
          <div className="pie-derecha">
            {/* La segunda salida NO depende del campo: en «cargar gas» el boton principal necesita
                un importe y el secundario es devolver lo que ya hay, que no lo necesita. Atarlos
                al mismo `listo` dejaba el secundario muerto con el campo vacio. */}
            {pet.secundario && (
              <button className="btn"
                onClick={() => lanzar(() => pet.secundario!.ejecutar())}>
                {pet.secundario.texto}
              </button>
            )}
            <button className={`btn ${pet.peligro ? "peligro" : "principal"}`}
              onClick={() => lanzar(() => pet.ejecutar(txt))}
              disabled={!listo} data-foco={campo ? undefined : true}>
              {pet.boton}
            </button>
          </div>
        </>
      }
    >
      <p className="detalle" style={{ margin: 0, fontSize: 14 }}>{resolver(pet.descripcion, valor)}</p>

      {campo && campo.tipo === "check" && (
        <button
          type="button" role="switch" aria-checked={valor === 1} data-foco
          className={`opcion-sw ${valor === 1 ? "on" : ""}`}
          onClick={() => setTxt(valor === 1 ? "0" : "1")}
        >
          <span className="sw" aria-hidden><span className="dot" /></span>
          <span className="ot">
            <span className="ot-lbl">{campo.etiqueta}</span>
            {campo.ayuda && <span className="ot-ayuda">{campo.ayuda}</span>}
          </span>
        </button>
      )}

      {campo && campo.tipo !== "check" && (
        <div className="campo-modal">
          <label>
            <span className="et">{campo.etiqueta}</span>
            {campo.tipo === "cantidad" ? (
              <span className="caja">
                <input
                  type="number" min="0" step="any" inputMode="decimal"
                  value={txt} onChange={(e) => setTxt(e.target.value)}
                  placeholder="0" autoFocus data-foco
                />
                {campo.sufijo && <span className="suf">{campo.sufijo}</span>}
                {campo.max != null && campo.max > 0 && (
                  <button type="button" className="todo"
                    onClick={() => setTxt(campo.maxTexto ?? String(campo.max))}>
                    {campo.maxEtiqueta ?? t("Todo")}
                  </button>
                )}
              </span>
            ) : (
              <span className="caja rango">
                <input
                  type="range" min={1} max={100} step={1}
                  value={txt === "" ? (campo.inicial ?? 50) : txt}
                  onChange={(e) => setTxt(e.target.value)}
                  data-foco
                />
                <output>{txt === "" ? (campo.inicial ?? 50) : txt}%</output>
              </span>
            )}
          </label>
          {campo.ayuda && <p className="detalle" style={{ margin: 0 }}>{campo.ayuda}</p>}
        </div>
      )}

      {(() => {
        const d = pet.detalles ? resolver(pet.detalles, valor) : [];
        if (!d.length) return null;
        return (
          <div className="detalles">
            {d.map(([k, val]) => {
              // Un total (QUEDA PARA TI, CIERRAS CON…) se marca solo: la clave va en mayúsculas.
              const esTotal = /[A-ZÁÉÍÓÚ]/.test(k) && k === k.toUpperCase();
              // El valor suele traer "cifra · matiz": la cifra manda, el matiz va en tenue.
              const i = val.indexOf("·");
              const cifra = (i >= 0 ? val.slice(0, i) : val).trim();
              const nota = i >= 0 ? val.slice(i + 1).trim() : "";
              const neg = cifra.startsWith("−") || cifra.startsWith("-");
              return (
                <div key={k} className={`fila-det ${esTotal ? "total" : ""}`}>
                  <span className="k">{k}</span>
                  <span className="v">
                    <b className={neg ? "neg" : ""}>{cifra}</b>
                    {nota && <span className="nt">{nota}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        const a = pet.aviso ? resolver(pet.aviso, valor) : null;
        return a ? <div className="aviso error"><p className="detalle">{a}</p></div> : null;
      })()}

      <p className="detalle" style={{ margin: 0 }}>
        {t("Tu wallet te va a pedir la firma. Nada se mueve hasta que la des.")}
      </p>
    </Modal>
  ) : null;

  return {
    pedir: (p: Peticion) => {
      const t = p.campo?.tipo;
      setTxt(
        t === "porcentaje" ? String(p.campo!.inicial ?? 50)
        : t === "check" ? String(p.campo!.inicial ?? 0)
        : ""
      );
      setPet(p);
    },
    modal,
    confirmando: !!pet,
  };
}
